import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, CreditCard, Building2, QrCode, Copy, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { donationApi, formatApiError } from '../services/api';

const DonationModal = ({ isOpen, onClose, paymentInfo }) => {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [message, setMessage] = useState('');
  const [copiedBank, setCopiedBank] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const quickAmounts = [100, 500, 1000, 5000, 10000];

  const resetForm = () => {
    setAmount(''); setName(''); setEmail(''); setAnonymous(false); setMessage('');
  };

  const validateForm = () => {
    if (!name.trim() || !email.trim() || !amount || parseInt(amount, 10) <= 0) {
      toast({
        title: 'Maklumat tidak lengkap',
        description: 'Sila isi nama, emel dan jumlah infaq.',
      });
      return false;
    }
    return true;
  };

  const recordDonation = async (method) => {
    const payload = {
      name: name.trim(),
      email: email.trim(),
      amount: parseInt(amount, 10),
      method,
      is_anonymous: anonymous,
      message: message.trim() || null,
    };
    return donationApi.create(payload);
  };

  const handleToyyibInfaq = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await recordDonation('toyyibpay');
      toast({
        title: 'Infaq direkodkan!',
        description: 'Anda akan diarahkan ke Toyyib Pay. Resit akan dihantar selepas pengesahan.',
      });
      window.open(paymentInfo.toyyibPayLink, '_blank');
      resetForm();
      onClose();
    } catch (err) {
      toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualInfaq = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await recordDonation('manual');
      toast({
        title: 'Terima kasih!',
        description: 'Infaq anda direkodkan. Sila hantar slip ke WhatsApp admin untuk pengesahan & resit.',
      });
      resetForm();
      onClose();
    } catch (err) {
      toast({ title: 'Ralat', description: formatApiError(err.response?.data?.detail) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyBankAccount = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(paymentInfo.accountNumber);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = paymentInfo.accountNumber;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedBank(true);
      setTimeout(() => setCopiedBank(false), 2000);
      toast({ title: 'Disalin!', description: 'Nombor akaun telah disalin ke clipboard' });
    } catch (err) {
      toast({ title: 'Nombor Akaun', description: paymentInfo.accountNumber });
    }
  };

  if (!isOpen) return null;

  // Shared donor details form
  const DonorForm = (
    <div className="space-y-4" data-testid="donor-form">
      <div>
        <Label className="text-sm font-semibold text-gray-700 mb-3 block">Pilih Jumlah Cepat</Label>
        <div className="grid grid-cols-5 gap-2">
          {quickAmounts.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => setAmount(amt.toString())}
              className={`py-3 px-1 rounded-xl border-2 font-semibold text-sm transition-all ${
                amount === amt.toString()
                  ? 'border-purple-600 bg-purple-50 text-purple-600'
                  : 'border-gray-200 hover:border-purple-300 text-gray-700'
              }`}
            >
              {amt}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label htmlFor="amount" className="text-sm font-semibold text-gray-700 mb-2 block">Jumlah Infaq (RM)</Label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">RM</span>
          <Input
            id="amount"
            type="number"
            data-testid="infaq-amount-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            className="pl-12 h-12 text-lg border-2 rounded-xl"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="name" className="text-sm font-semibold text-gray-700 mb-2 block">Nama Penuh</Label>
        <Input
          id="name"
          data-testid="infaq-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ahmad bin Abdullah"
          className="h-11 border-2 rounded-xl"
        />
      </div>

      <div>
        <Label htmlFor="email" className="text-sm font-semibold text-gray-700 mb-2 block">Emel (untuk resit)</Label>
        <Input
          id="email"
          type="email"
          data-testid="infaq-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ahmad@example.com"
          className="h-11 border-2 rounded-xl"
        />
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="anonymous"
          data-testid="infaq-anonymous-checkbox"
          checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)}
          className="w-5 h-5 rounded border-2 border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <Label htmlFor="anonymous" className="text-gray-700 cursor-pointer">Infaq secara tanpa nama</Label>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="donation-modal"
            >
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-t-3xl z-10">
                <button
                  onClick={onClose}
                  data-testid="close-modal-button"
                  className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Heart className="w-6 h-6" />
                  </div>
                  <h2 className="text-3xl font-bold">Jom Infaq</h2>
                </div>
                <p className="text-purple-100">Setiap sumbangan anda sangat bermakna</p>
              </div>

              {/* Content */}
              <div className="p-8">
                {/* Donor details */}
                <div className="mb-8">{DonorForm}</div>

                <div className="border-t border-gray-100 pt-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Kaedah Pembayaran</h3>
                  <Tabs defaultValue="toyyibpay" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                      <TabsTrigger value="toyyibpay" className="flex items-center gap-2" data-testid="tab-toyyibpay">
                        <CreditCard className="w-4 h-4" />
                        Toyyib Pay
                      </TabsTrigger>
                      <TabsTrigger value="manual" className="flex items-center gap-2" data-testid="tab-manual">
                        <Building2 className="w-4 h-4" />
                        Manual
                      </TabsTrigger>
                    </TabsList>

                    {/* ToyyibPay Tab */}
                    <TabsContent value="toyyibpay">
                      <div className="space-y-6">
                        <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6">
                          <p className="text-gray-600 text-sm mb-4">
                            Platform pembayaran Islam yang selamat dan mudah. Anda akan diarahkan ke halaman Toyyib Pay untuk membuat pembayaran.
                          </p>
                          <Button
                            onClick={handleToyyibInfaq}
                            disabled={submitting}
                            data-testid="toyyibpay-submit-button"
                            className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                          >
                            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Jom Infaq dengan Toyyib Pay'}
                          </Button>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                          <p className="text-sm text-green-800">
                            <strong>✅ Potongan Cukai:</strong> {paymentInfo.taxExemption}
                          </p>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Manual Tab */}
                    <TabsContent value="manual" className="space-y-6">
                      <div className="bg-purple-50 border-2 border-purple-200 rounded-2xl p-6">
                        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-purple-600" />
                          Maklumat Bank
                        </h3>
                        <div className="space-y-3">
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Nama Bank</div>
                            <div className="font-semibold text-gray-900">{paymentInfo.bankName}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Nombor Akaun</div>
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-gray-900 font-mono text-lg">{paymentInfo.accountNumber}</div>
                              <button onClick={handleCopyBankAccount} className="p-2 hover:bg-purple-100 rounded-lg transition-colors">
                                {copiedBank ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-purple-600" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600 mb-1">Rujukan</div>
                            <div className="font-semibold text-gray-900">{paymentInfo.reference}</div>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleManualInfaq}
                        disabled={submitting}
                        data-testid="manual-submit-button"
                        className="w-full h-14 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg font-semibold rounded-xl"
                      >
                        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Saya Sudah Buat Pembayaran - Rekod Infaq'}
                      </Button>

                      <a href={paymentInfo.whatsappAdmin} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="outline" className="w-full border-2 border-green-600 text-green-700 hover:bg-green-50 rounded-xl">
                          Hantar Slip via WhatsApp: {paymentInfo.contactAdmin}
                        </Button>
                      </a>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <p className="text-sm text-blue-800 mb-2"><strong>📱 Selepas membuat pembayaran:</strong></p>
                        <ul className="text-sm text-blue-800 space-y-1 ml-4">
                          <li>• Klik "Rekod Infaq" di atas</li>
                          <li>• WhatsApp slip pembayaran ke {paymentInfo.contactAdmin}</li>
                          <li>• Resit rasmi akan dihantar ke emel selepas pengesahan</li>
                        </ul>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DonationModal;
